import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FaArrowRight, FaPaperPlane, FaGlobeAfrica, FaUsers, FaCalendarAlt, FaMapMarkerAlt } from "react-icons/fa";
import "../css/styles.css";
import { API_BASE, parseJsonResponse } from "../api";

const destinations = [
  {
    id: "kenya",
    name: "Kenya",
    tagline: " Kenya Tours & Private Holidays",
    route: "/kenya",
    description:
      "From the sweeping savannahs of the Masai Mara to the snow-capped peak of Mount Kenya, discover a land where wildlife roams free and every landscape tells a story. Kenya offers unmatched safari diversity — big cats in golden grasslands, flamingo-filled lakes, and pristine Indian Ocean beaches.",
    highlights: ["Masai Mara", "Amboseli", "Tsavo", "Diani Beach", "Lake Nakuru", "Samburu"],
    heroImage:
      "https://res.cloudinary.com/dn39bkkiq/image/upload/q_auto,f_auto,w_1200/v1776344927/Discover_Kenya_qctdrk.jpg",
    accent: "#075f39",
  },
  {
    id: "tanzania",
    name: "Tanzania",
    tagline: "Tanzania Tours & Private Holidays",
    route: "/tanzania",
    description:
      "Home to the Serengeti, Ngorongoro Crater, and the mighty Kilimanjaro — Tanzania is the ultimate safari destination. Witness the Great Migration, explore ancient calderas teeming with wildlife, and experience the warmth of local Maasai culture.",
    highlights: ["Serengeti", "Ngorongoro Crater", "Kilimanjaro", "Tarangire", "Lake Manyara", "Arusha"],
    heroImage:
      "https://res.cloudinary.com/dn39bkkiq/image/upload/q_auto,f_auto,w_1200/v1776344794/Discover_Tanzania_2_ocbuzp.jpg",
    accent: "#421c46",
  },
  {
    id: "zanzibar",
    name: "Zanzibar",
    tagline: "Zanzibar Holidays & Stays",
    description:
      "Turquoise waters, white-sand beaches, and the intoxicating aromas of a spice island paradise. Zanzibar is the perfect finale to any East African adventure — explore Stone Town's winding alleys, swim with dolphins, or simply unwind in a beachfront villa.",
    highlights: ["Stone Town", "Nungwi Beach", "Mnemba Atoll", "Spice Farms", "Prison Island", "Paje"],
    heroImage:
      "https://res.cloudinary.com/dn39bkkiq/image/upload/q_auto,f_auto,w_1200/v1776344763/Discover_Zanzibar_i5umkp.jpg",
    accent: "#0a7e8c",
  },
];

const Destinations = () => {
  const [activeTab, setActiveTab] = useState("kenya");
  const [form, setForm] = useState({
    name: "",
    email: "",
    destination: "",
    travelers: "",
    travelDate: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const active = destinations.find((d) => d.id === activeTab);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/api/booking`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          destination: form.destination || active.name,
          travelers: form.travelers,
          date: form.travelDate,
          message: form.message,
        }),
      });
      const data = await parseJsonResponse(res);
      if (!res.ok) throw new Error(data.error || data.message || `Request failed ${res.status}`);
      setSubmitted(true);
      setForm({ name: "", email: "", destination: "", travelers: "", travelDate: "", message: "" });
      setTimeout(() => setSubmitted(false), 5000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="destinations-page">
      {/* ===== HERO ===== */}
      <section className="dest-hero">
        <div className="dest-hero-bg">
          <img
            src={active.heroImage}
            alt={active.name}
            key={active.id}
          />
          <div className="dest-hero-overlay" />
        </div>

        <div className="dest-hero-content">
          <span className="dest-hero-eyebrow">Explore East Africa</span>

          {/* Destination Tabs */}
          <div className="dest-tabs">
            {destinations.map((d) => (
              <button
                key={d.id}
                className={`dest-tab ${activeTab === d.id ? "active" : ""}`}
                onClick={() => setActiveTab(d.id)}
              >
                <FaMapMarkerAlt className="dest-tab-icon" />
                {d.name}
              </button>
            ))}
          </div>

          <h1 className="dest-hero-title" key={active.id + "-title"}>
            {active.tagline}
          </h1>

          <div className="dest-hero-buttons">
            <button
              onClick={() =>
                document.getElementById("dest-plan-form")?.scrollIntoView({ behavior: "smooth" })
              }
              className="hero-cta primary"
            >
              Start Designing Your Trip <FaArrowRight className="btn-icon" />
            </button>
            <Link to={active.route || "/tours"} className="hero-cta secondary">
              Explore Our {active.name} Trips
            </Link>
          </div>
        </div>
      </section>

      {/* ===== DESTINATION DETAIL ===== */}
      <section className="dest-detail">
        <div className="dest-detail-inner">
          <div className="dest-detail-text">
            <span className="section-tag">{active.name}</span>
            <h2 className="dest-detail-heading">
              Discover {active.name}
            </h2>
            <p className="dest-detail-description">{active.description}</p>

            <div className="dest-highlights">
              {active.highlights.map((h, i) => (
                <span className="dest-highlight-chip" key={i}>
                  <FaMapMarkerAlt /> {h}
                </span>
              ))}
            </div>

            <div className="dest-detail-actions">
              <Link to={active.route || "/tours"} className="hero-cta primary">
                View {active.name} Safaris <FaArrowRight className="btn-icon" />
              </Link>
            </div>
          </div>

          <div className="dest-detail-visual">
            <div className="dest-detail-card">
              <img
                src={active.heroImage}
                alt={active.name}
                key={active.id + "-card"}
              />
              <div className="dest-detail-card-overlay">
                <h3>{active.name}</h3>
                <span>{active.highlights.length}+ Destinations</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== ALL DESTINATIONS GRID ===== */}
      <section className="dest-grid-section">
        <span className="section-tag centered">Our Destinations</span>
        <h2 className="section-heading">Where Will Your Adventure Begin?</h2>
        <p className="section-subheading dest-grid-sub">
          Three extraordinary countries, one unforgettable journey
        </p>

        <div className="dest-grid">
          {destinations.map((d) => (
            <button
              key={d.id}
              className={`dest-grid-card ${activeTab === d.id ? "ring" : ""}`}
              onClick={() => {
                setActiveTab(d.id);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
            >
              <img src={d.heroImage} alt={d.name} />
              <div className="dest-grid-card-overlay" />
              <div className="dest-grid-card-content">
                <FaMapMarkerAlt className="dest-grid-pin" />
                <h3>{d.name}</h3>
                <p>{d.tagline}</p>
                <span className="dest-grid-card-cta">
                  Explore <FaArrowRight />
                </span>
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* ===== CTA FORM ===== */}
      <section className="cta-section cta-clean" id="dest-plan-form">
        <div className="cta-content">
          <span className="cta-eyebrow">Your Safari, Your Way</span>
          <h2>Start Designing Your Trip</h2>
          <p>
            Tell us about your dream {active.name} adventure and we'll craft a
            personalised safari just for you.
          </p>

          {error && (
            <div className="modern-form-error">
              <p>{error}</p>
            </div>
          )}

          {submitted ? (
            <div className="modern-form-success">
              <div className="success-checkmark">&#10003;</div>
              <h3>Request Sent!</h3>
              <p>Our safari experts will reach out shortly.</p>
            </div>
          ) : (
            <form className="modern-form cta-inline-form" onSubmit={handleSubmit}>
              <div className="modern-form-grid">
                <div className="modern-form-group">
                  <label htmlFor="dest-name">Full Name</label>
                  <input
                    id="dest-name"
                    type="text"
                    name="name"
                    placeholder="Your full name"
                    value={form.name}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="modern-form-group">
                  <label htmlFor="dest-email">Email Address</label>
                  <input
                    id="dest-email"
                    type="email"
                    name="email"
                    placeholder="you@example.com"
                    value={form.email}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="modern-form-group">
                  <label htmlFor="dest-destination">
                    <FaGlobeAfrica className="field-icon" /> Destination
                  </label>
                  <select
                    id="dest-destination"
                    name="destination"
                    value={form.destination}
                    onChange={handleChange}
                  >
                    <option value="">Pick a destination</option>
                    <option value="Kenya">Kenya</option>
                    <option value="Tanzania">Tanzania</option>
                    <option value="Zanzibar">Zanzibar</option>
                    <option value="Kenya & Tanzania">Kenya & Tanzania Combo</option>
                    <option value="Not Sure">Not Sure Yet</option>
                  </select>
                </div>
                <div className="modern-form-group">
                  <label htmlFor="dest-travelers">
                    <FaUsers className="field-icon" /> Travelers
                  </label>
                  <select
                    id="dest-travelers"
                    name="travelers"
                    value={form.travelers}
                    onChange={handleChange}
                  >
                    <option value="">How many?</option>
                    <option value="1">Solo</option>
                    <option value="2">2 People</option>
                    <option value="3-5">3 – 5</option>
                    <option value="6-10">6 – 10</option>
                    <option value="10+">10+ Group</option>
                  </select>
                </div>
                <div className="modern-form-group">
                  <label htmlFor="dest-date">
                    <FaCalendarAlt className="field-icon" /> Travel Date
                  </label>
                  <input
                    id="dest-date"
                    type="date"
                    name="travelDate"
                    value={form.travelDate}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="modern-form-group full-width">
                <label htmlFor="dest-message">Tell Us More</label>
                <textarea
                  id="dest-message"
                  name="message"
                  rows="4"
                  placeholder="Any special interests, budget, or questions?"
                  value={form.message}
                  onChange={handleChange}
                />
              </div>
              <button type="submit" className="modern-form-submit cta-form-submit" disabled={loading}>
                {loading ? (
                  <span className="spinner-wrapper">
                    <span className="spinner" /> Sending...
                  </span>
                ) : (
                  <>
                    <FaPaperPlane /> Design My {active.name} Trip
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
};

export default Destinations;
